import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageCircle, Loader2, ChevronDown, ChevronUp, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchChat = ({ matchId, matchStatus, participants }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Check if chat is available for this match status
  const chatEnabled = ['pending_acceptance', 'active', 'in_progress', 'awaiting_proof', 'disputed'].includes(matchStatus);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    if (!matchId || !chatEnabled) return;
    
    try {
      const res = await axios.get(`${API}/matches/${matchId}/chat`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error fetching chat:', error);
    } finally {
      setLoading(false);
    }
  }, [matchId, chatEnabled]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (chatEnabled) {
      fetchMessages();
      
      pollIntervalRef.current = setInterval(() => {
        fetchMessages();
      }, 5000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [fetchMessages, chatEnabled]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/matches/${matchId}/chat/send`,
        { message: newMessage.trim() },
        { withCredentials: true }
      );
      
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isMyMessage = (senderId) => senderId === user?.user_id;

  if (!chatEnabled) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl overflow-hidden"
      data-testid="match-chat"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
        data-testid="chat-toggle"
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
          <span className="font-semibold">Match Chat</span>
          {messages.length > 0 && (
            <span className="px-2 py-0.5 bg-[#00F0FF]/20 text-[#00F0FF] text-xs rounded-full">
              {messages.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-white/50" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/50" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Messages Container */}
            <div className="h-64 overflow-y-auto p-4 space-y-3 bg-black/20" data-testid="chat-messages">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-white/40">
                  <MessageCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs">Start the conversation!</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, index) => (
                    <motion.div
                      key={msg.message_id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isMyMessage(msg.sender_id) ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-xl p-3 ${
                          isMyMessage(msg.sender_id)
                            ? 'bg-[#7000FF] text-white'
                            : 'bg-white/10 text-white'
                        }`}
                      >
                        {/* Sender name for opponent messages */}
                        {!isMyMessage(msg.sender_id) && (
                          <div className="flex items-center gap-1 mb-1">
                            <User className="w-3 h-3 text-[#00F0FF]" />
                            <span className="text-xs text-[#00F0FF] font-medium">
                              {msg.sender_name}
                            </span>
                          </div>
                        )}
                        <p className="text-sm break-words">{msg.message}</p>
                        <p className={`text-xs mt-1 ${
                          isMyMessage(msg.sender_id) ? 'text-white/60' : 'text-white/40'
                        }`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-white/5 border-white/10 focus:border-[#00F0FF]"
                  maxLength={1000}
                  disabled={sending}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                  data-testid="chat-send-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/30 mt-2">
                Chat is available during active matches and for dispute resolution
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MatchChat;
